import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import { TotalResults } from '@shared/components/ui/TotalResults';
import { usePageNavigation } from '@shared/components/navigation/usePageNavigation';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal/ConfirmModal';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/components/ui/shadcn/tabs';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgGridColumns, useBlockedShipmentsColumns } from '../../hooks/useAgGridColumns';
import { useBlockedShipmentsMain } from '../../hooks/useBlockedShipmentsMain';
import { useBlockShipment } from '../../hooks/useBlockShipment';
import { useBulkInvoiceShipments } from '../../hooks/useBulkInvoiceShipments';
import { useBulkInvoiceShipmentsByFilters } from '../../hooks/useBulkInvoiceShipmentsByFilters';
import { useInvoiceShipment } from '../../hooks/useInvoiceShipment';

import { useShipmentsMain } from '../../hooks/useShipmentsMain';
import { useUnblockShipment } from '../../hooks/useUnblockShipment';
import { useUpdateShipment } from '../../hooks/useUpdateShipment';
import { BlockedShipmentsAgGridTable, InvoicesOrdersAgGridTable } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { EligibleShipmentsTopBarActions, FiltersSection } from '../interaction';
import {
  BlockShipmentModal,
  BlockShipmentModalRef,
  EditarMasivoModal,
  EditarMasivoModalRef,
  EditarPrefacturaModal,
  EditarPrefacturaModalRef,
  PrefacturarModal,
  PrefacturarModalRef,
} from '../modals';

const NOOP_HEADER_ACTION = () => {};

export const Main = () => {
  const { t } = useTranslation('invoicesOrdersModule');
  const prefacturarModalRef = useRef<PrefacturarModalRef>(null);
  const editarPrefacturaModalRef = useRef<EditarPrefacturaModalRef>(null);
  const editarMasivoModalRef = useRef<EditarMasivoModalRef>(null);
  const confirmBlockModalRef = useRef<ConfirmModalRef>(null);
  const confirmInvoiceModalRef = useRef<ConfirmModalRef>(null);
  const confirmBulkInvoiceModalRef = useRef<ConfirmModalRef>(null);
  const blockShipmentModalRef = useRef<BlockShipmentModalRef>(null);
  const currency = useCurrency();
  const { blockShipment } = useBlockShipment();
  const { unblockShipment } = useUnblockShipment();
  const { invoiceShipment } = useInvoiceShipment();
  const { bulkInvoiceShipments } = useBulkInvoiceShipments();
  const { bulkInvoiceByFilters } = useBulkInvoiceShipmentsByFilters();
  const { updateShipment } = useUpdateShipment();

  // useInvoicesPermissions(); // canCreatePreinvoices - reserved for future use

  // Estado para controlar que el spinner de carga inicial se muestre solo una vez
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Estado para el tab activo
  const [activeTab, setActiveTab] = useState<'eligible' | 'blocked'>('eligible');

  // Hook que maneja toda la lógica de negocio de shipments elegibles
  const {
    // Estados
    selectedShipments,
    selectedColumns,

    // Datos
    shipments,
    isLoading,
    isFetching,
    error,
    total,

    // Props para componentes
    shipmentsViewProps,

    // Handlers
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    handleClearSelection,
    refetch,

    // Props para infinite scroll
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,

    // Handlers de selección
    handleSelectionChanged,

    // Estado de filtros
    hasActiveFilters,
    filterConditions,
    searchTerm,
    filterOptions,
    exportFilters,
  } = useShipmentsMain();

  // Hook que maneja toda la lógica de negocio de shipments bloqueados
  const {
    // Estados
    selectedColumns: blockedSelectedColumns,
    sorting: blockedSorting,

    // Datos
    blockedShipments,
    isLoading: isLoadingBlocked,
    isFetching: isFetchingBlocked,
    error: errorBlocked,
    total: totalBlocked,

    // Props para componentes
    // blockedShipmentsViewProps, // reserved for future use

    // Handlers
    handleFilterChange: handleFilterChangeBlocked,
    handleSearch: handleSearchBlocked,
    handleColumnsChange: handleColumnsChangeBlocked,
    handleSortingChange: handleSortingChangeBlocked,
    refetch: refetchBlocked,

    // Props para infinite scroll
    fetchNextPage: fetchNextPageBlocked,
    hasNextPage: hasNextPageBlocked,
    isFetchingNextPage: isFetchingNextPageBlocked,

    // Estado de filtros
    hasActiveFilters: hasActiveFiltersBlocked,
    filterConditions: filterConditionsBlocked,
    searchTerm: searchTermBlocked,
  } = useBlockedShipmentsMain();

  // 🎯 Efecto para desactivar el spinner de carga inicial después del primer fetch
  useEffect(() => {
    // Si isLoading pasa de true a false, significa que la carga inicial terminó.
    if (!isLoading && isInitialLoad && activeTab === 'eligible') {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad, activeTab]);

  // 🎯 Efecto para desactivar el spinner de carga inicial para bloqueados
  useEffect(() => {
    if (!isLoadingBlocked && isInitialLoad && activeTab === 'blocked') {
      setIsInitialLoad(false);
    }
  }, [isLoadingBlocked, isInitialLoad, activeTab]);

  // 🎯 Verificar si el usuario es 3PL (solo ellos ven la columna de Cliente/Team)
  const { getTeamType } = usePermissions();
  const hasTeamData = getTeamType() === 'tenant_3pl';

  // 🎯 Obtener columnas para el ColumnSelector usando el hook de AG Grid
  const { tanStackColumns } = useAgGridColumns({ hasTeamData, selectedColumns });
  const { tanStackColumns: blockedTanStackColumns } = useBlockedShipmentsColumns({
    hasTeamData,
    selectedColumns: blockedSelectedColumns,
  });

  // Columnas para el selector: usar las columnas de AG Grid convertidas a TanStack
  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);
  const blockedSelectorColumns = useMemo(() => blockedTanStackColumns, [blockedTanStackColumns]);

  // Handlers para las acciones del SelectionToolbar
  const handlePrefacturar = useCallback(() => {
    // Abrir modal con los datos de selección y filtros
    prefacturarModalRef.current?.open({
      selectedCount: selectedShipments.size,
      totalResults: total, // 🎯 Total de shipments según filtros aplicados
      filters: filterConditions,
      filterOptions: filterOptions || [],
      onConfirm: () => {
        // 🎯 Columnas que son parámetros de body (no advanced_filters)
        const URL_PARAM_COLUMNS = new Set([
          'courier_id',
          'team_id',
          'shop_id',
          'payment_method_id',
          'period',
          'status_ids',
          'shipment_status_ids',
          'pre_invoice_id',
          'invoice_id',
        ]);

        // 🎯 Convertir filterConditions a formato advanced_filters (excluyendo params de URL)
        const conditionsForAdvancedFilter = filterConditions.filter((c) => !URL_PARAM_COLUMNS.has(c.column));
        const advancedFilters = {
          filters: conditionsForAdvancedFilter.map((condition) => {
            const operator = condition.action;
            let value: any = condition.value;

            // 🎯 Los operadores 'in' y 'not_in' requieren valores en array
            if (operator === 'in' || operator === 'not_in') {
              if (!Array.isArray(value)) {
                // Convertir string separado por comas a array
                if (typeof value === 'string') {
                  value = value
                    .split(',')
                    .map((v: string) => v.trim())
                    .filter(Boolean);
                } else {
                  value = [value];
                }
              }
            }

            return {
              column: condition.column,
              operator: operator,
              value: value,
            };
          }),
          logic: 'AND', // Por defecto usamos AND para los filtros
        };

        // 🎯 Extraer filtros como params de body (courier_ids, period, team_ids, etc.)
        const columnToParam: Record<string, string> = {
          courier_id: 'courier_ids',
          team_id: 'team_ids',
          shop_id: 'shop_ids',
          payment_method_id: 'payment_method_ids',
          period: 'period',
          status_ids: 'status_ids',
          shipment_status_ids: 'shipment_status_ids',
          pre_invoice_id: 'pre_invoice_ids',
          invoice_id: 'invoice_ids',
        };
        const toNumArray = (v: unknown): number[] => {
          if (v == null) return [];
          const arr = Array.isArray(v) ? v : [v];
          return arr.map((x) => (typeof x === 'number' ? x : parseInt(String(x), 10))).filter((n) => !Number.isNaN(n));
        };
        const additionalFilters: {
          courier_ids?: number[];
          period?: string;
          team_ids?: number[];
          shop_ids?: number[];
          payment_method_ids?: number[];
          status_ids?: number[];
          shipment_status_ids?: number[];
          pre_invoice_ids?: number[];
          invoice_ids?: number[];
        } = {};
        for (const c of filterConditions) {
          const param = columnToParam[c.column];
          if (!param || ['is_null', 'is_not_null'].includes(c.action)) continue;
          if (param === 'period') {
            const val = c.value;
            const periodVal = Array.isArray(val) ? ((val[0] as string) ?? '') : String(val ?? '');
            if (periodVal) additionalFilters.period = periodVal;
          } else {
            const nums = toNumArray(c.value);
            if (nums.length > 0) (additionalFilters as any)[param] = nums;
          }
        }

        // 🎯 Llamar a la API de prefacturación por filtros (con filtros adicionales opcionales)
        bulkInvoiceByFilters(
          advancedFilters,
          () => {
            // Cerrar modal y refetch después del éxito
            prefacturarModalRef.current?.close();
            handleClearSelection();
            refetch();
          },
          additionalFilters,
        );
      },
    });
  }, [selectedShipments, filterConditions, total, filterOptions, bulkInvoiceByFilters, handleClearSelection, refetch]);

  const handlePrefacturarRef = useRef(handlePrefacturar);
  handlePrefacturarRef.current = handlePrefacturar;

  const onTopBarPrefacturar = useCallback(() => {
    handlePrefacturarRef.current();
  }, []);

  const breadcrumbs = useMemo(
    () => [
      { label: t('breadcrumb.invoices', 'Facturación') },
      {
        label: <span className="text-title">{t('breadcrumb.orders', 'Pedidos')}</span>,
        _key: 'eligible-shipments-orders',
      },
    ],
    [t],
  );

  const headerActions = useMemo(() => {
    if (activeTab !== 'eligible') {
      return [];
    }

    return [
      {
        id: 'eligible-shipments-topbar-actions',
        label: '',
        onClick: NOOP_HEADER_ACTION,
        component: <EligibleShipmentsTopBarActions onPrefacturar={onTopBarPrefacturar} prefacturarDisabled={false} />,
      },
    ];
  }, [activeTab, onTopBarPrefacturar]);

  usePageNavigation({ breadcrumbs, headerActions });

  // Handler para editar un shipment individual desde el dropdown
  const handleEditSingleShipment = useCallback(
    (shipmentId: number) => {
      // Buscar el shipment en los datos cargados
      const shipment = shipments.find((s: any) => s.id === shipmentId);

      if (!shipment) {
        return;
      }

      // 🎯 Formatear precio con 2 decimales y moneda dinámica
      const formatPrice = (value: number | undefined) => formatCurrencyAmount(value ?? 0, currency);

      // 🎯 Buscar líneas de shipment por concepto (usando base_amount - sin impuestos)
      const shippingLine = shipment.lines?.find((l: any) => l.concept === 'SHIPPING');
      const preparationLine = shipment.lines?.find((l: any) => l.concept === 'PREPARATION');
      const codLine = shipment.lines?.find((l: any) => l.concept === 'COD');

      // 🎯 Calcular total sumando base_amount de todas las líneas (sin impuestos)
      const total = shipment.lines?.reduce((sum: number, line: any) => sum + (line.base_amount || 0), 0) || 0;

      // 🎯 Determinar origen y destino según el tipo de envío
      // - RETURN (devolución): origen = cliente, destino = Beeping
      // - SHIPPING (envío): origen = Beeping, destino = cliente
      const isReturn = shipment.type === 'RETURN';

      // 🎯 Acceder a datos adicionales de la API (no tipados en el modelo)
      const orderData = shipment.order as any;

      // Dirección del cliente (desde order.customer.address)
      const customerAddr = orderData?.customer?.address;
      const customerAddress = {
        country: customerAddr?.shipping_country || customerAddr?.shipping_country_code || shipment.country || '',
        city: customerAddr?.shipping_city || shipment.city || '',
        zip: customerAddr?.shipping_zip || shipment.zip || '',
      };

      // Dirección de Beeping (almacén)
      const beepingAddress = {
        country: 'Beeping',
        city: '',
        zip: '',
      };

      // Asignar origen y destino según el tipo
      const origin = isReturn ? customerAddress : beepingAddress;
      const destination = isReturn ? beepingAddress : customerAddress;

      // 🎯 Mapear líneas para la API (solo base_amount - sin impuestos)
      const lines = (shipment.lines || []).map((line: any) => ({
        id: line.id,
        concept: line.concept,
        base_amount: line.base_amount || 0,
      }));

      // Guardar shipmentId y lines para usar en el callback
      const currentShipmentId = shipment.id;

      // Abrir modal con los datos del shipment (precios sin impuestos)
      editarPrefacturaModalRef.current?.open({
        reference: shipment.order?.ref || '',
        shop: shipment.order?.shop?.name || '',
        client: shipment.order?.team?.name || '',
        paymentMethod: orderData?.payment_method_method || '',
        internalId: shipment.order_id?.toString() || '',
        shippingPrice: formatPrice(shippingLine?.base_amount),
        preparationPrice: formatPrice(preparationLine?.base_amount),
        codPrice: formatPrice(codLine?.base_amount),
        totalPrice: formatPrice(total),
        type: shipment.type || '',
        logisticStatus: shipment.order?.shipment_status?.name || '',
        shippingOrReturn:
          shipment.type === 'SHIPPING' ? 'Envío' : shipment.type === 'RETURN' ? 'Devolución' : 'Reenvío',
        courier: shipment.courier?.name || orderData?.shipment?.courier?.name || '',
        orderWeight: shipment.weight_internal_kg ? `${shipment.weight_internal_kg} kg` : '0 kg',
        numberOfLines: shipment.num_items?.toString() || shipment.lines?.length?.toString() || '0',
        originCountry: origin.country,
        originMunicipality: origin.city,
        originPostalCode: origin.zip,
        destinationCountry: destination.country,
        destinationMunicipality: destination.city,
        destinationPostalCode: destination.zip,
        onSave: async (data) => {
          // Extraer valores numéricos de los precios formateados
          const extractNumber = (value: string): number => {
            const cleaned = value.replace(/[^\d,.-]/g, '').replace(',', '.');
            return parseFloat(cleaned) || 0;
          };

          const shippingAmount = extractNumber(data.shippingPrice);
          const preparationAmount = extractNumber(data.preparationPrice);
          const codAmount = extractNumber(data.codPrice);
          const weightKg = extractNumber(data.orderWeight);
          const numItems = parseInt(data.numberOfLines, 10) || 0;

          // Construir payload para la API
          const linesPayload = lines.map((line: { id: number; concept: string; base_amount: number }) => {
            let newBaseAmount = line.base_amount;
            if (line.concept === 'SHIPPING') {
              newBaseAmount = shippingAmount;
            } else if (line.concept === 'PREPARATION') {
              newBaseAmount = preparationAmount;
            } else if (line.concept === 'COD') {
              newBaseAmount = codAmount;
            }
            return {
              id: line.id,
              base_amount: newBaseAmount,
            };
          });

          const payload = {
            weight_internal_kg: weightKg,
            num_items: numItems,
            lines: linesPayload,
          };

          await updateShipment(currentShipmentId, payload, () => {
            refetch();
          });
        },
      });
    },
    [shipments, currency, refetch, updateShipment],
  );

  // Handler para bloquear un shipment individual desde el dropdown
  const handleBlockSingleShipment = useCallback(
    (shipmentId: number) => {
      // Abrir modal de formulario (motivo + comentario)
      blockShipmentModalRef.current?.open(shipmentId, ({ shipment_block_reason_id, comment }) => {
        blockShipment(
          shipmentId,
          () => {
            // 🎯 Cerrar modal y refetch ambas listas después del éxito
            blockShipmentModalRef.current?.close();
            refetch(); // Refrescar lista de elegibles (para que desaparezca el bloqueado)
            refetchBlocked(); // Refrescar lista de bloqueados (para que aparezca el nuevo bloqueado)
          },
          { shipment_block_reason_id, comment },
        );
      });
    },
    [blockShipment, refetch, refetchBlocked],
  );

  // Handler para desbloquear un shipment bloqueado desde el dropdown
  const handleUnblockSingleShipment = useCallback(
    (blockId: number) => {
      // Mostrar confirmación antes de desbloquear
      confirmBlockModalRef.current?.open(
        () => {
          // Usuario confirmó: ejecutar desbloqueo
          unblockShipment(blockId, () => {
            // Refetch de ambas listas después del desbloqueo exitoso
            refetch(); // Refrescar lista de elegibles (para que aparezca el desbloqueado)
            refetchBlocked(); // Refrescar lista de bloqueados (para que desaparezca el desbloqueado)
          });
        },
        undefined,
        t('confirmModals.unblockShipment.title', 'Desbloquear shipment'),
        t('confirmModals.unblockShipment.message', '¿Estás seguro de que deseas desbloquear este shipment?'),
        t('confirmModals.unblockShipment.confirm', 'Desbloquear'),
        t('confirmModals.unblockShipment.cancel', 'Cancelar'),
      );
    },
    [unblockShipment, refetch, refetchBlocked, t],
  );

  // Handler para prefacturar un shipment individual desde el dropdown
  const handleInvoiceSingleShipment = useCallback(
    (shipmentId: number) => {
      // Mostrar confirmación antes de prefacturar
      confirmInvoiceModalRef.current?.open(
        () => {
          // Usuario confirmó: ejecutar prefacturación
          invoiceShipment(shipmentId, () => {
            // Refetch de datos después de la prefacturación exitosa
            refetch();
          });
        },
        undefined,
        t('confirmModals.invoiceShipment.title', 'Prefacturar shipment'),
        t('confirmModals.invoiceShipment.message', '¿Estás seguro de que deseas prefacturar este shipment?'),
        t('confirmModals.invoiceShipment.confirm', 'Prefacturar'),
        t('confirmModals.invoiceShipment.cancel', 'Cancelar'),
      );
    },
    [invoiceShipment, refetch, t],
  );

  // Handler para prefacturar múltiples shipments seleccionados
  const handleBulkInvoiceShipments = useCallback(() => {
    // Obtener los IDs de los shipments seleccionados
    const selectedIds = Array.from(selectedShipments);

    if (selectedIds.length === 0) {
      return;
    }

    // Mostrar confirmación antes de prefacturar
    confirmBulkInvoiceModalRef.current?.open(
      () => {
        // Usuario confirmó: ejecutar prefacturación masiva
        bulkInvoiceShipments(selectedIds, () => {
          // Limpiar selección y refetch de datos después de la prefacturación exitosa
          handleClearSelection();
          refetch();
        });
      },
      undefined,
      t('confirmModals.bulkInvoiceShipment.title', 'Prefacturar shipments'),
      t('confirmModals.bulkInvoiceShipment.message', {
        count: selectedIds.length,
        defaultValue: `¿Estás seguro de que deseas prefacturar ${selectedIds.length} shipments?`,
      }),
      t('confirmModals.bulkInvoiceShipment.confirm', 'Prefacturar'),
      t('confirmModals.bulkInvoiceShipment.cancel', 'Cancelar'),
    );
  }, [selectedShipments, bulkInvoiceShipments, handleClearSelection, refetch, t]);

  // Handler para edición masiva de shipments seleccionados
  const handleBulkEditShipments = useCallback(() => {
    const selectedIds = Array.from(selectedShipments);

    if (selectedIds.length === 0) {
      return;
    }

    // Obtener los shipments seleccionados con todos los datos necesarios
    const shipmentsForEdit = shipments
      .filter((s: any) => selectedIds.includes(s.id))
      .map((s: any) => ({
        id: s.id,
        reference: s.order?.ref || `#${s.id}`,
        shop: s.order?.shop?.name || '',
        client: s.order?.team?.name || '',
        lines: (s.lines || []).map((l: any) => ({
          id: l.id,
          concept: l.concept,
          base_amount: l.base_amount || 0,
        })),
        weight_internal_kg: s.weight_internal_kg || 0,
        num_items: s.num_items || 0,
      }));

    // Abrir modal de edición masiva
    editarMasivoModalRef.current?.open({
      shipments: shipmentsForEdit,
      onSuccess: () => {
        handleClearSelection();
        refetch();
      },
    });
  }, [selectedShipments, shipments, handleClearSelection, refetch]);

  const renderShipments = () => {
    /*if (isMobile) {
      return <CardList {...shipmentsViewProps} orders={shipments as any} showActions={true} />;
    }*/

    // 🎯 Usar AG Grid para la vista de escritorio
    return (
      <InvoicesOrdersAgGridTable
        {...shipmentsViewProps}
        orders={shipments as any}
        selectedOrders={selectedShipments}
        onClearSelection={handleClearSelection}
        selectedColumns={selectedColumns}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onSelectionChanged={handleSelectionChanged}
        onEditSingleShipment={handleEditSingleShipment}
        onBlockSingleShipment={handleBlockSingleShipment}
        onInvoiceSingleShipment={handleInvoiceSingleShipment}
        onBulkInvoiceShipments={handleBulkInvoiceShipments}
        onBulkEditShipments={handleBulkEditShipments}
      />
    );
  };

  // 🎯 Muestra el spinner SOLO en la carga inicial
  const currentIsLoading = activeTab === 'eligible' ? isLoading : isLoadingBlocked;
  const currentError = activeTab === 'eligible' ? error : errorBlocked;
  const currentRefetch = activeTab === 'eligible' ? refetch : refetchBlocked;

  if (isInitialLoad && currentIsLoading) {
    return <LoadingState message={t('loading.message', 'Cargando envíos...')} />;
  }

  if (currentError) {
    return <ErrorState error={currentError} onRetry={currentRefetch} />;
  }

  return (
    <div className="px-6 pt-4">
      {/* Tabs para alternar entre Elegibles y Bloqueados */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as 'eligible' | 'blocked')}
        className="w-full"
      >
        <TabsList className="mb-4">
          <TabsTrigger value="eligible">{t('tabs.eligible', 'Elegibles')}</TabsTrigger>
          <TabsTrigger value="blocked">{t('tabs.blocked', 'Bloqueados')}</TabsTrigger>
        </TabsList>

        {/* Tab de Elegibles */}
        <TabsContent value="eligible" className="mt-0">
          {/* Header con filtros y botón crear */}
          <div className="mb-4">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-3 flex-wrap">
                <FiltersSection
                  handleSearch={handleSearch}
                  onFilterChange={handleFilterChange}
                  columns={selectorColumns}
                  selectedColumns={selectedColumns}
                  onColumnsChange={handleColumnsChange}
                  filterConditions={filterConditions}
                  hasTeamData={hasTeamData}
                  searchTerm={searchTerm}
                  exportFilters={exportFilters}
                />
              </div>
              <TotalResults total={total} />
            </div>
          </div>

          {/* Estado vacío */}
          {!isInitialLoad && shipments.length === 0 && (
            <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.ORDERS} />
          )}

          {/* Tabla visible solo si hay resultados */}
          {!isInitialLoad && total > 0 && (
            <div
              className={`transition-opacity duration-300 mb-4 mt-4 ${isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
            >
              {renderShipments()}
            </div>
          )}
        </TabsContent>

        {/* Tab de Bloqueados */}
        <TabsContent value="blocked" className="mt-0">
          {/* Header con filtros */}
          <div className="mb-4">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-3 flex-wrap">
                <FiltersSection
                  handleSearch={handleSearchBlocked}
                  onFilterChange={handleFilterChangeBlocked}
                  columns={blockedSelectorColumns}
                  selectedColumns={blockedSelectedColumns}
                  onColumnsChange={handleColumnsChangeBlocked}
                  filterConditions={filterConditionsBlocked}
                  hasTeamData={hasTeamData}
                  searchTerm={searchTermBlocked}
                />
              </div>
              <TotalResults total={totalBlocked} />
            </div>
          </div>

          {/* Estado vacío */}
          {!isInitialLoad && blockedShipments.length === 0 && (
            <EmptyView type={hasActiveFiltersBlocked ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.ORDERS} />
          )}

          {/* Tabla visible solo si hay resultados */}
          {!isInitialLoad && totalBlocked > 0 && (
            <div
              className={`transition-opacity duration-300 mb-4 mt-4 ${isFetchingBlocked && !isFetchingNextPageBlocked ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
            >
              <BlockedShipmentsAgGridTable
                blockedShipments={blockedShipments}
                selectedColumns={[...blockedSelectedColumns, 'actions']}
                fetchNextPage={fetchNextPageBlocked}
                hasNextPage={hasNextPageBlocked}
                isFetchingNextPage={isFetchingNextPageBlocked}
                sorting={blockedSorting}
                onSortingChange={handleSortingChangeBlocked}
                onUnblockShipment={handleUnblockSingleShipment}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal de prefacturación */}
      <PrefacturarModal ref={prefacturarModalRef} />

      {/* Modal de edición de prefactura */}
      <EditarPrefacturaModal ref={editarPrefacturaModalRef} />

      {/* Modal de edición masiva */}
      <EditarMasivoModal ref={editarMasivoModalRef} />

      {/* Modal de confirmación para bloquear shipment */}
      <ConfirmModal ref={confirmBlockModalRef} />
      <BlockShipmentModal ref={blockShipmentModalRef} />

      {/* Modal de confirmación para prefacturar shipment individual */}
      <ConfirmModal ref={confirmInvoiceModalRef} />

      {/* Modal de confirmación para prefacturar shipments en bloque */}
      <ConfirmModal ref={confirmBulkInvoiceModalRef} />
    </div>
  );
};
